'use client';

import * as React from 'react';
import { X, Loader2, AlertCircle } from 'lucide-react';
import { api, Plan, CreateTenantData, ApiError } from '@/lib/api';

interface AddPlatformModalProps {
  clientId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export default function AddPlatformModal({ clientId, onClose, onSuccess }: AddPlatformModalProps) {
  const [plans, setPlans] = React.useState<Plan[]>([]);
  const [isLoadingPlans, setIsLoadingPlans] = React.useState(true);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [generatedPassword, setGeneratedPassword] = React.useState<string | null>(null);

  const [formData, setFormData] = React.useState({
    platformName: '',
    slug: '',
    planId: '',
    adminName: '',
    adminEmail: '',
  });

  React.useEffect(() => {
    const loadPlans = async () => {
      try {
        setIsLoadingPlans(true);
        const data = await api.getPlans();
        setPlans(data.filter(plan => plan.isActive));
        if (data.length > 0) {
          setFormData(prev => ({ ...prev, planId: data[0].id }));
        }
      } catch (err) {
        const apiError = err as ApiError;
        setError(apiError.message || 'Erro ao carregar planos');
      } finally {
        setIsLoadingPlans(false);
      }
    };

    loadPlans();
  }, []);

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handlePlatformNameChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      platformName: value,
      slug: generateSlug(value),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.platformName || !formData.slug || !formData.planId) {
      setError('Preencha todos os campos obrigatórios');
      return;
    }

    try {
      setIsSubmitting(true);

      const createData: CreateTenantData = {
        clientId: clientId,
        platformName: formData.platformName,
        slug: formData.slug,
        planId: formData.planId,
        admin: formData.adminName && formData.adminEmail ? {
          name: formData.adminName,
          email: formData.adminEmail,
        } : undefined,
        sendWelcomeEmail: false,
      };

      const result = await api.createTenant(createData);

      // If a password was generated, show it to the user
      if (result.generatedPassword) {
        setGeneratedPassword(result.generatedPassword);
      } else {
        // No password generated, close immediately
        onSuccess();
      }
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Erro ao criar plataforma');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (generatedPassword) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-2xl max-w-md w-full p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Plataforma Criada com Sucesso!</h3>
            <button
              onClick={() => {
                setGeneratedPassword(null);
                onSuccess();
              }}
              className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              A plataforma foi criada com sucesso. Anote a senha gerada abaixo:
            </p>

            <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Email</span>
                  <span className="text-sm font-mono text-gray-900">{formData.adminEmail}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Senha</span>
                  <code className="text-sm font-mono text-gray-900 bg-white px-2 py-1 rounded border border-gray-200">
                    {generatedPassword}
                  </code>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 rounded-xl border border-yellow-200 p-4">
              <p className="text-xs text-yellow-800">
                ⚠️ Guarde esta senha em local seguro. Ela não será exibida novamente.
              </p>
            </div>

            <button
              onClick={() => {
                setGeneratedPassword(null);
                onSuccess();
              }}
              className="w-full px-4 py-2.5 rounded-xl bg-[#0057FF] text-white text-sm font-medium hover:bg-[#0046CC] transition-colors"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Adicionar Nova Plataforma</h3>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 rounded-xl border border-red-100 p-4">
              <div className="flex items-center gap-3">
                <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                <p className="text-sm text-red-600">{error}</p>
              </div>
            </div>
          )}

          {/* Platform Info */}
          <div className="space-y-4">
            <h4 className="font-medium text-gray-900">Informações da Plataforma</h4>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nome da Plataforma *
              </label>
              <input
                type="text"
                value={formData.platformName}
                onChange={(e) => handlePlatformNameChange(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#0057FF]/20 focus:border-[#0057FF] transition-all"
                placeholder="Ex: Comunidade Suvinil"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Slug (URL) *
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData(prev => ({ ...prev, slug: generateSlug(e.target.value) }))}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#0057FF]/20 focus:border-[#0057FF] transition-all font-mono text-sm"
                  placeholder="comunidade-suvinil"
                  required
                />
                <span className="text-sm text-gray-500">.nozcomunidades.com.br</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Plano *
              </label>
              {isLoadingPlans ? (
                <div className="flex items-center gap-2 text-gray-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm">Carregando planos...</span>
                </div>
              ) : (
                <select
                  value={formData.planId}
                  onChange={(e) => setFormData(prev => ({ ...prev, planId: e.target.value }))}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#0057FF]/20 focus:border-[#0057FF] transition-all"
                  required
                >
                  {plans.map((plan) => (
                    <option key={plan.id} value={plan.id}>
                      {plan.name} - R$ {plan.price.toFixed(2)}/{plan.billingCycle === 'monthly' ? 'mês' : 'ano'}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {/* Admin Info */}
          <div className="space-y-4 border-t border-gray-100 pt-6">
            <h4 className="font-medium text-gray-900">Administrador (Opcional)</h4>
            <p className="text-sm text-gray-500">
              Se preenchido, será criado um usuário administrador para esta plataforma.
            </p>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nome do Administrador
              </label>
              <input
                type="text"
                value={formData.adminName}
                onChange={(e) => setFormData(prev => ({ ...prev, adminName: e.target.value }))}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#0057FF]/20 focus:border-[#0057FF] transition-all"
                placeholder="João Silva"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email do Administrador
              </label>
              <input
                type="email"
                value={formData.adminEmail}
                onChange={(e) => setFormData(prev => ({ ...prev, adminEmail: e.target.value }))}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#0057FF]/20 focus:border-[#0057FF] transition-all"
                placeholder="joao@exemplo.com"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 pt-6 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting || isLoadingPlans}
              className="flex-1 px-4 py-2.5 rounded-xl bg-[#0057FF] text-white text-sm font-medium hover:bg-[#0046CC] transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Criando...
                </>
              ) : (
                'Criar Plataforma'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
